import React from 'react';

import { useIntl } from 'react-intl';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { safeFormatMessage } from 'src/utils/intl';

import type { TooltipContentProps } from 'recharts';
import type { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';

export default function StatisticTooltip<TValue extends ValueType, TName extends NameType>({
    active,
    payload,
    label,
}: TooltipContentProps<TValue, TName>) {
    const { formatMessage, formatNumber } = useIntl();
    const settingsData = React.useContext(SettingsContext);

    // An entry without a value has nothing to read, so it is left out - the same way the legend
    // leaves out the categories the chart draws nothing for
    const entriesWithValue: Payload<TValue, TName>[] = (payload ?? []).filter(
        (entry: Payload<TValue, TName>) =>
            entry.value !== null && entry.value !== undefined && Number(entry.value) !== 0,
    );

    if (active && entriesWithValue.length) {
        return (
            <div className="w-full max-w-56 rounded-sm border bg-white p-2 text-sm shadow-xs">
                <p className="font-medium text-gray-500">{label}</p>
                {entriesWithValue.map((entry) => {
                    return (
                        <p key={Math.random()} className="font-medium text-gray-800" style={{ color: entry.color }}>
                            {safeFormatMessage(
                                formatMessage,
                                String(entry.name ?? '').replaceAll('_', '-'),
                                String(entry.name),
                            )}
                            :{' '}
                            {entry.name && entry.dataKey === 'amount'
                                ? formatNumber(Number(entry.value) / 100, {
                                      style: 'currency',
                                      currency: entry?.payload?.currency ?? settingsData.default_currency,
                                  })
                                : entry.value}
                        </p>
                    );
                })}
            </div>
        );
    }

    return null;
}
